import { createApi } from '@reduxjs/toolkit/query/react'

import { DocumentBatch, DocumentBatchCreate } from '../../models/DocumentBatch'

import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetDocumentBatchByIdRequest extends BaseRequest {
  documentBatchId: string
}

export interface CreateDocumentBatchRequest extends BaseRequest {
  payload: DocumentBatchCreate
}

export interface DeleteDocumentBatchRequest extends BaseRequest {
  documentBatchId: string
}

export const documentBatchesApi = createApi({
  reducerPath: 'documentBatchesApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['DocumentBatch'],
  endpoints: (builder) => ({
    getDocumentBatches: builder.query<BasePageResponse<DocumentBatch[]>, BasePageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/document-batches`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
        }
      },
      providesTags: [{ type: 'DocumentBatch', id: 'LIST' }],
    }),
    getAllDocumentBatches: builder.query<BaseResponse<DocumentBatch[]>, BaseRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<DocumentBatch>(
          {
            url: `/accounts/${request.accountId}/document-batches`,
            params: {},
          },
          fetchWithBQ,
        )
      },
      providesTags: [{ type: 'DocumentBatch', id: 'LIST' }],
    }),
    getDocumentBatchById: builder.query<DocumentBatch, GetDocumentBatchByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/document-batches/${request.documentBatchId}`,
      transformResponse: (response: BaseResponse<DocumentBatch>) => {
        return response.data
      },
      providesTags: (_result, _error, request) => [{ type: 'DocumentBatch', id: request.documentBatchId }],
    }),
    createDocumentBatch: builder.mutation<DocumentBatch, CreateDocumentBatchRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/document-batches`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentBatch>) => {
        return response.data
      },
      invalidatesTags: [{ type: 'DocumentBatch', id: 'LIST' }],
    }),
    deleteDocumentBatch: builder.mutation<void, DeleteDocumentBatchRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/document-batches/${request.documentBatchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'DocumentBatch', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetDocumentBatchesQuery,
  useLazyGetAllDocumentBatchesQuery,
  useGetAllDocumentBatchesQuery,
  useGetDocumentBatchByIdQuery,
  useLazyGetDocumentBatchByIdQuery,
  useCreateDocumentBatchMutation,
  useDeleteDocumentBatchMutation,
} = documentBatchesApi
